import {Injectable} from '@angular/core';
import {registerLocaleData} from '@angular/common';
import localeEnGB from '@angular/common/locales/en-GB';
import localeSv from '@angular/common/locales/sv';
import localeFi from '@angular/common/locales/fi';
import localeNb from '@angular/common/locales/nb';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localePt from '@angular/common/locales/pt';
import localeKo from '@angular/common/locales/ko';
import localeJa from '@angular/common/locales/ja';
import localeZh from '@angular/common/locales/zh';
import {enGB, sv, fi, nb, nn, de, es, fr, pt, ko, ja, zhCN} from 'date-fns/locale';
import ILocaleViewData = NCVIB.app.common.localization.ILocaleViewData;
import {CoreApiService} from "../core/core-api-service";

@Injectable({
    providedIn: "root"
})
export class LocalizationService {
    private currentLocale: string | null = null;

    constructor(private core: CoreApiService) {
    }

    public async loadLocale(): Promise<string> {
        return new Promise(async (resolve) => {
            let locale = this.loadLocaleFromLocalStorage();

            if (locale) {
                this.currentLocale = locale;
            } else {
                this.currentLocale = await this.loadCurrentUserLocale() ?? "en-GB";
                this.saveLocaleToLocalStorage(this.currentLocale);
            }

            this.registerLocaleData(this.currentLocale);

            resolve(this.currentLocale);
        });
    }

    public loadLocaleFromLocalStorage(fallback:string|null = null, toAngular: boolean = false): string|null {
        let locale = localStorage.getItem('locale') ?? fallback;
        return toAngular ? this.localeToAngular(locale!) : locale;
    }

    public saveLocaleToLocalStorage(locale: string): void {
        localStorage.setItem('locale', locale);
    }

    private loadCurrentUserLocale(): Promise<string> {
        return new Promise((resolve) => {
            this.core.get<ILocaleViewData>(`api/v2/localization/get-current-locale`, "Get current locale")
                .subscribe(currentLocale => {
                    resolve(currentLocale.localeName);
                });
        });
    }

    private registerLocaleData(currentLocale: string) {

        switch (currentLocale) {
            case "sv":
                registerLocaleData(localeSv);
                break;
            case "fi":
                registerLocaleData(localeFi);
                break;
            case "no":
            case "nb":
            case "nn":
                registerLocaleData(localeNb);
                break;
            case "de":
                registerLocaleData(localeDe);
                break;
            case "es":
                registerLocaleData(localeEs);
                break;
            case "fr":
                registerLocaleData(localeFr);
                break;
            case "pt":
                registerLocaleData(localePt);
                break;
            case "ko-KR":
                registerLocaleData(localeKo);
                break;
            case "ja-JP":
                registerLocaleData(localeJa);
                break;
            case "zh-CN":
                registerLocaleData(localeZh);
                break;
            default:
                registerLocaleData(localeEnGB);
        }
    }

    private localeToAngular(locale: string) {
        // What language code we save in our database differs from the language codes angular use
        switch (locale) {
            case "no":
                return "nb";
            case "ko-KR":
                return "ko";
            case "ja-JP":
                return "ja";
            case "zh-CN":
                return "zh";
            default:
                return locale
        }
    }

    public getCurrentLocaleForDateFns() {
        let locale = this.getCurrentLocale();

        const localeMapping: { [key: string]: any } = {
            'sv': sv,
            'en': enGB,
            'fi': fi,
            'no': nb,
            'nb': nb,
            'nn': nn,
            'de': de,
            'es': es,
            'fr': fr,
            'pt': pt,
            'ko': ko,
            'ja': ja,
            'zh': zhCN
        };

        for (const key in localeMapping) {
            if (locale?.startsWith(key)) {
                return localeMapping[key];
            }
        }

        return locale;
    }

    public getCurrentLocale(): string {
        if (!!this.currentLocale) {
            return this.currentLocale!;
        }

        if (typeof window === "undefined" || typeof window.navigator === "undefined") {
            return "en-GB";
        }

        const wn = window.navigator as any;
        let locale = wn.languages ? wn.languages[0] : "en-GB";
        locale = locale || wn.language || wn.browserLanguage || wn.userLanguage;

        return this.mapLocaleToDatabaseLanguageCode(locale);
    }

    public mapLocaleToDatabaseLanguageCode(locale: string) {
        if (locale.startsWith("sv")) {
            return "sv";
        }
        if (locale.startsWith("en")) {
            return "en-GB";
        }
        if (locale.startsWith("fi")) {
            return "fi";
        }
        if (locale.startsWith("nb") || locale.startsWith("nn")) {
            return "no";
        }
        if (locale.startsWith("de")) {
            return "de";
        }
        if (locale.startsWith("es")) {
            return "es";
        }
        if (locale.startsWith("fr")) {
            return "fr";
        }
        if (locale.startsWith("pt")) {
            return "pt";
        }
        if (locale.startsWith("ko")) {
            return "ko-KR";
        }
        if (locale.startsWith("ja")) {
            return "ja-JP";
        }
        if (locale.startsWith("zh")) {
            return "zh-CN";
        }

        return locale;
    }
}
